<template>
  <div
    class="bracket-display-wrapper"
    :class="{
      'bracket-display-compact': compactPrint,
      'bracket-display-team': isTeamBracket
    }"
  >
    <div v-if="!placementOnly" class="bracket-display">
      <div
        v-for="round in rounds"
        :key="round.number"
        class="bracket-round"
        :class="`round-${round.number}`"
      >
        <div class="round-header">
          <h4>{{ getRoundName(round.number) }}</h4>
        </div>
        <div class="round-matches" :style="getRoundMatchesStyle(round)">
          <div
            v-for="match in round.matches"
            :key="match.id"
            class="match-container"
            :data-match-id="match.id"
            :ref="`match-${match.id}`"
            :style="getMatchPositionStyle(match)"
          >
            <div
              class="match-box"
              :class="{
                'is-clickable': isMatchClickable(match),
                'is-completed': match.winnerRegId !== null && !isAdmin,
                'is-bye': match.isBye,
                'is-disabled': !isLocked && !isAdmin
              }"
              @click="handleMatchClick(match)"
            >
              <div
                v-if="getMatchLabel(match)"
                class="match-label-inline"
              >
                {{ $tc('tournament.match') }} {{ getMatchLabel(match) }}
              </div>
              <div
                class="competitor competitor-1"
                :class="{
                  'is-bye': match.isBye && !match.competitorReg,
                  'is-winner': match.competitorReg && match.winnerRegId === match.competitorRegId
                }"
              >
              <span v-if="match.competitorReg">
                <template v-if="match.competitorReg.isTeam && match.competitorReg.teamMembers && match.competitorReg.teamMembers.length > 0">
                  <div class="team-entry">
                    <div class="team-entry-club">{{ getCompetitorClubName(match.competitorReg) || '—' }}</div>
                    <div class="team-member-names">
                      <div
                        v-for="(member, index) in match.competitorReg.teamMembers"
                        :key="index"
                        class="team-member-name"
                      >
                        <span v-if="member.assignedNumber" class="team-member-number">#{{ member.assignedNumber }}</span>
                        {{ member.firstName }} {{ member.lastName }} ({{ member.isMale ? 'M' : 'Ž' }})
                      </div>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <span class="competitor-name-block">
                    <span class="competitor-name-line">{{ getCompetitorName(match.competitorReg) }}</span>
                    <span v-if="getCompetitorClubName(match.competitorReg)" class="competitor-club-line">{{ getCompetitorClubName(match.competitorReg) }}</span>
                  </span>
                </template>
                <span v-if="isMedalHolder(match.competitorReg)"> 🏅</span>
                <span
                  v-if="match.competitorReg && match.competitorReg.seed"
                  class="seed-badge"
                >
                  #{{ match.competitorReg.seed }}
                </span>
              </span>
                <span v-else-if="isFirstRound(match) && match.isBye" class="bye-text">BYE</span>
                <span v-else-if="getPlaceholderText(match, 1)" class="placeholder-text">
                  {{ getPlaceholderText(match, 1) }}
                </span>
                <span v-else class="empty-slot">-</span>
              </div>
              <div class="vs-divider">vs</div>
              <div
                class="competitor competitor-2"
                :class="{
                  'is-bye': match.isBye && !match.competitorTwoReg,
                  'is-winner': match.competitorTwoReg && match.winnerRegId === match.competitorTwoRegId
                }"
              >
              <span v-if="match.competitorTwoReg">
                <template v-if="match.competitorTwoReg.isTeam && match.competitorTwoReg.teamMembers && match.competitorTwoReg.teamMembers.length > 0">
                  <div class="team-entry">
                    <div class="team-entry-club">{{ getCompetitorClubName(match.competitorTwoReg) || '—' }}</div>
                    <div class="team-member-names">
                      <div
                        v-for="(member, index) in match.competitorTwoReg.teamMembers"
                        :key="index"
                        class="team-member-name"
                      >
                        <span v-if="member.assignedNumber" class="team-member-number">#{{ member.assignedNumber }}</span>
                        {{ member.firstName }} {{ member.lastName }} ({{ member.isMale ? 'M' : 'Ž' }})
                      </div>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <span class="competitor-name-block">
                    <span class="competitor-name-line">{{ getCompetitorName(match.competitorTwoReg) }}</span>
                    <span v-if="getCompetitorClubName(match.competitorTwoReg)" class="competitor-club-line">{{ getCompetitorClubName(match.competitorTwoReg) }}</span>
                  </span>
                </template>
                <span v-if="isMedalHolder(match.competitorTwoReg)"> 🏅</span>
                <span
                  v-if="match.competitorTwoReg && match.competitorTwoReg.seed"
                  class="seed-badge"
                >
                  #{{ match.competitorTwoReg.seed }}
                </span>
              </span>
                <span v-else-if="isFirstRound(match) && match.isBye" class="bye-text">BYE</span>
                <span v-else-if="getPlaceholderText(match, 2)" class="placeholder-text">
                  {{ getPlaceholderText(match, 2) }}
                </span>
                <span v-else class="empty-slot">-</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Winner card after Final -->
      <div
        v-if="finalMatch"
        class="bracket-round winner-round"
      >
        <div class="round-header">
          <h4>{{ $tc('tournament.winner') }}</h4>
        </div>
        <div class="round-matches winner-container" :style="getWinnerContainerStyle()">
          <div class="match-container winner-match" :style="getWinnerMatchStyle()">
            <div
              class="match-box winner-box"
              :class="{
                'is-clickable': isAdmin,
                'is-completed': finalWinner !== null
              }"
              @click="handleWinnerClick"
            >
              <div class="competitor winner-competitor">
                <span v-if="finalWinner">
                  <template v-if="finalWinner.isTeam && finalWinner.teamMembers && finalWinner.teamMembers.length > 0">
                    <div class="team-entry">
                      <div class="team-entry-club">{{ getCompetitorClubName(finalWinner) || '—' }}</div>
                      <div class="team-member-names">
                        <div
                          v-for="(member, index) in finalWinner.teamMembers"
                          :key="index"
                          class="team-member-name"
                        >
                          <span v-if="member.assignedNumber" class="team-member-number">#{{ member.assignedNumber }}</span>
                          {{ member.firstName }} {{ member.lastName }} ({{ member.isMale ? 'M' : 'Ž' }})
                        </div>
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    {{ getCompetitorName(finalWinner) }}
                  </template>
                  <span v-if="isMedalHolder(finalWinner)"> 🏅</span>
                </span>
                <span v-else class="empty-slot">-</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- SVG overlay for bracket connector lines -->
      <svg
        v-if="connectorPaths.length > 0"
        class="bracket-connectors"
        :viewBox="`0 0 ${svgWidth} ${svgHeight}`"
        preserveAspectRatio="none"
      >
        <path
          v-for="(path, index) in connectorPaths"
          :key="`connector-${index}`"
          :d="path.d"
          class="connector-line"
          :data-source-match="path.sourceMatchId"
          :data-target-match="path.targetMatchId"
        />
      </svg>
    </div>

    <match-result-modal
      :match="selectedMatch"
      :visible.sync="showModal"
      :is-admin="isAdmin"
      :bracket="bracket"
      :bracket-competitors="bracketCompetitors"
      @result-submitted="handleResultSubmitted"
    />

    <winner-edit-modal
      :visible.sync="showWinnerModal"
      :final-match="finalMatch"
      :competitors="bracketCompetitors"
      @winner-saved="handleWinnerSaved"
    />

    <!-- Placement section -->
    <div class="placement-section">
      <h4 class="placement-title">Plasman:</h4>
      <div class="placement-fields">
        <div v-if="placementPositions >= 1" class="placement-field">
          <label class="placement-label">1. mesto:</label>
          <el-select
            v-model="placement.first"
            placeholder="Izaberite takmičara"
            class="placement-select"
            clearable
            :multiple="placementMulti"
            @change="savePlacement"
          >
            <el-option
              v-for="competitor in availableForFirst"
              :key="competitor._id || competitor.id"
              :label="getPlacementCompetitorLabel(competitor, 'first')"
              :value="competitor._id || competitor.id"
            />
          </el-select>
        </div>
        <div v-if="placementPositions >= 2" class="placement-field">
          <label class="placement-label">2. mesto:</label>
          <el-select
            v-model="placement.second"
            placeholder="Izaberite takmičara"
            class="placement-select"
            clearable
            :multiple="placementMulti"
            @change="savePlacement"
          >
            <el-option
              v-for="competitor in availableForSecond"
              :key="competitor._id || competitor.id"
              :label="getPlacementCompetitorLabel(competitor, 'second')"
              :value="competitor._id || competitor.id"
            />
          </el-select>
        </div>
        <div v-if="placementPositions >= 3" class="placement-field">
          <label class="placement-label">3. mesto:</label>
          <el-select
            v-model="placement.third"
            placeholder="Izaberite takmičara"
            class="placement-select"
            clearable
            :multiple="placementMulti"
            @change="savePlacement"
          >
            <el-option
              v-for="competitor in availableForThird"
              :key="competitor._id || competitor.id"
              :label="getPlacementCompetitorLabel(competitor, 'third')"
              :value="competitor._id || competitor.id"
            />
          </el-select>
        </div>
        <div v-if="placementPositions >= 4" class="placement-field">
          <label class="placement-label">3. mesto #2:</label>
          <el-select
            v-model="placement.third2"
            placeholder="Izaberite takmičara"
            class="placement-select"
            clearable
            :multiple="placementMulti"
            @change="savePlacement"
          >
            <el-option
              v-for="competitor in availableForThird2"
              :key="competitor._id || competitor.id"
              :label="getPlacementCompetitorLabel(competitor, 'third2')"
              :value="competitor._id || competitor.id"
            />
          </el-select>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MatchResultModal from './MatchResultModal'
import WinnerEditModal from './WinnerEditModal'
import { derivePlacementFromMatches, placementPositionsForCount } from '@/common/bracket-placement'

export default {
  name: 'BracketDisplay',

  components: {
    MatchResultModal,
    WinnerEditModal
  },

  props: {
    bracket: {
      type: Object,
      required: true
    },
    isLocked: {
      type: Boolean,
      default: false
    },
    isAdmin: {
      type: Boolean,
      default: false
    },
    competitors: {
      type: Array,
      default: () => []
    },
    teams: {
      type: Array,
      default: () => []
    },
    compactPrint: {
      type: Boolean,
      default: false
    },
    printTeamLayout: {
      type: Boolean,
      default: false
    },
    placementOnly: {
      type: Boolean,
      default: false
    },
    placementMulti: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      showModal: false,
      showWinnerModal: false,
      selectedMatch: null,
      matchLayouts: {},
      roundHeights: {},
      baseMatchGap: 24,
      connectorPaths: [],
      svgWidth: 0,
      svgHeight: 0,
      placement: {
        first: null,
        second: null,
        third: null,
        third2: null
      },
      // Tracks match state BEFORE we mutate it via placement propagation,
      // so we can revert when podium selections are removed/changed.
      // Shape: { [matchId]: { matchId, round, matchNumber, before: { competitorRegId, competitorTwoRegId, winnerRegId } } }
      placementAutoEdits: {},
      // Set when the admin just changed a match result (tree click or winner modal). The next bracket
      // reload then lets the tree overwrite the podium instead of only filling blanks.
      pendingAuthoritativePlacementSync: false,
      placementSyncBusy: false
    }
  },

  computed: {
    matches () {
      return this.bracket.matches || []
    },

    rounds () {
      const roundsMap = {}

      this.matches.forEach(match => {
        if (!roundsMap[match.round]) {
          roundsMap[match.round] = {
            number: match.round,
            matches: []
          }
        }
        roundsMap[match.round].matches.push(match)
      })

      // Sort matches within each round by matchNumber
      Object.keys(roundsMap).forEach(roundNum => {
        roundsMap[roundNum].matches.sort((a, b) => {
          return (a.matchNumber || 0) - (b.matchNumber || 0)
        })
      })

      // Convert to array and sort by round number
      return Object.values(roundsMap).sort((a, b) => a.number - b.number)
    },

    // Build a map of matches by ID for quick lookup
    matchesById () {
      const map = {}
      this.matches.forEach(match => {
        map[match.id] = match
      })
      return map
    },

    effectiveBaseMatchGap () {
      return this.compactPrint ? 6 : this.baseMatchGap
    },

    layoutRoundWidth () {
      return this.compactPrint ? 110 : 250
    },

    layoutGap () {
      return this.compactPrint ? 8 : 60
    },

    layoutPadding () {
      return this.compactPrint ? { left: 8, top: 8 } : { left: 40, top: 35 }
    },

    layoutRoundHeaderHeight () {
      return this.compactPrint ? 16 : 35
    },

    // Map ALL match IDs to their labels (A, B, C, etc.) - continuous across all rounds
    matchLabels () {
      const labels = {}
      let labelIndex = 0

      // Iterate through all rounds sequentially
      this.rounds.forEach(round => {
        round.matches.forEach(match => {
          labels[match.id] = String.fromCharCode(65 + labelIndex) // A, B, C, etc.
          labelIndex++
        })
      })

      return labels
    },

    // Map future match slots to their source match labels from the previous round
    // Returns object: { matchId: { competitor1Label: 'A', competitor2Label: 'B' } }
    // For Round 2: uses Round 1 labels (A, B, C, D)
    // For Round 3+: uses previous round labels (E, F, etc.)
    matchSlotSources () {
      const sources = {}

      // For each round after Round 1
      for (let i = 1; i < this.rounds.length; i++) {
        const round = this.rounds[i]

        round.matches.forEach(match => {
          if (!sources[match.id]) {
            sources[match.id] = {}
          }

          // Find which matches feed into this match (from previous round)
          const previousRound = this.rounds[i - 1]
          if (!previousRound) return

          // Find matches in previous round that feed into this match
          const sourceMatches = previousRound.matches.filter(m => m.nextMatchId === match.id)

          // Sort by matchNumber to ensure consistent ordering
          sourceMatches.sort((a, b) => (a.matchNumber || 0) - (b.matchNumber || 0))

          // Use labels from the previous round
          if (sourceMatches.length >= 1) {
            sources[match.id].competitor1Label = this.matchLabels[sourceMatches[0].id] || null
          }
          if (sourceMatches.length >= 2) {
            sources[match.id].competitor2Label = this.matchLabels[sourceMatches[1].id] || null
          }
        })
      }

      return sources
    },

    // Check if this bracket is for team disciplines
    isTeamBracket () {
      return this.bracket.teamDiscipline !== null &&
             this.bracket.teamDiscipline !== undefined &&
             this.bracket.teamDiscipline !== '' &&
             this.bracket.teamDiscipline !== 0
    },

    // Get the final match
    finalMatch () {
      if (this.rounds.length === 0) return null
      const finalRound = this.rounds[this.rounds.length - 1]
      return finalRound.matches[0] || null
    },

    // Get the winner of the final match
    finalWinner () {
      if (!this.finalMatch) return null

      // Primary source: actual final match winner
      if (this.finalMatch.winnerRegId) {
        if (this.finalMatch.winnerRegId === this.finalMatch.competitorRegId) {
          return this.finalMatch.competitorReg
        } else if (this.finalMatch.winnerRegId === this.finalMatch.competitorTwoRegId) {
          return this.finalMatch.competitorTwoReg
        }
      }

      // Fallback: if placement is assigned but the final match is still empty/undecided (common for 4+ competitors),
      // show 1st place in the winner card so UI reflects the saved standings.
      const placedFirst = this.bracket?.placement?.first
      if (placedFirst == null || placedFirst === '') return null
      const idStr = String(placedFirst)
      return this.bracketCompetitors.find(c => String(c._id || c.id) === idStr) || null
    },

    // Get all competitors in the bracket (from first round matches)
    bracketCompetitors () {
      const competitors = []
      const competitorIds = new Set()

      // Get competitors from the first round (eliminations)
      if (this.rounds.length > 0) {
        const firstRound = this.rounds[0]
        firstRound.matches.forEach(match => {
          if (match.competitorReg && !competitorIds.has(match.competitorRegId)) {
            // Store competitorReg with its ID for easy access
            const competitor = { ...match.competitorReg, _id: match.competitorRegId }
            competitors.push(competitor)
            competitorIds.add(match.competitorRegId)
          }
          if (match.competitorTwoReg && !competitorIds.has(match.competitorTwoRegId)) {
            // Store competitorReg with its ID for easy access
            const competitor = { ...match.competitorTwoReg, _id: match.competitorTwoRegId }
            competitors.push(competitor)
            competitorIds.add(match.competitorTwoRegId)
          }
        })
      }

      // Sort by assigned number if available, otherwise by name
      return competitors.sort((a, b) => {
        const numA = a.assignedNumber ? parseInt(a.assignedNumber) : 9999
        const numB = b.assignedNumber ? parseInt(b.assignedNumber) : 9999
        if (numA !== numB) return numA - numB

        const nameA = a.competitor ? `${a.competitor.firstName || ''} ${a.competitor.lastName || ''}`.trim() : ''
        const nameB = b.competitor ? `${b.competitor.firstName || ''} ${b.competitor.lastName || ''}`.trim() : ''
        return nameA.localeCompare(nameB)
      })
    },

    // Calculate number of placement positions based on number of competitors
    placementPositions () {
      return placementPositionsForCount(this.bracketCompetitors.length)
    },

    /**
     * Podium implied by the match tree. Null when the tree cannot say anything (no matches).
     * This is the "bracket → Plasman" direction; `propagatePlacementThroughBracket` is the inverse.
     */
    derivedPlacement () {
      return derivePlacementFromMatches(this.matches, this.placementPositions)
    },

    // Get list of selected placement IDs
    selectedPlacementIds () {
      const values = [
        this.placement.first,
        this.placement.second,
        this.placement.third,
        this.placement.third2
      ]
      if (!this.placementMulti) {
        return values.filter(id => id !== null && id !== undefined)
      }

      // Multi-select: flatten arrays
      return values
        .flatMap(v => Array.isArray(v) ? v : (v == null ? [] : [v]))
        .filter(id => id !== null && id !== undefined)
    },

    // Available competitors for first place (exclude other selections)
    availableForFirst () {
      return this.bracketCompetitors.filter(c => {
        const id = c._id || c.id
        if (!this.placementMulti) {
          return id === this.placement.first ||
                 !this.selectedPlacementIds.includes(id)
        }
        const selected = Array.isArray(this.placement.first) ? this.placement.first : []
        return selected.includes(id) || !this.selectedPlacementIds.includes(id)
      })
    },

    // Available competitors for second place (exclude other selections)
    availableForSecond () {
      return this.bracketCompetitors.filter(c => {
        const id = c._id || c.id
        if (!this.placementMulti) {
          return id === this.placement.second ||
                 !this.selectedPlacementIds.includes(id)
        }
        const selected = Array.isArray(this.placement.second) ? this.placement.second : []
        return selected.includes(id) || !this.selectedPlacementIds.includes(id)
      })
    },

    // Available competitors for third place (exclude other selections)
    availableForThird () {
      return this.bracketCompetitors.filter(c => {
        const id = c._id || c.id
        if (!this.placementMulti) {
          return id === this.placement.third ||
                 !this.selectedPlacementIds.includes(id)
        }
        const selected = Array.isArray(this.placement.third) ? this.placement.third : []
        return selected.includes(id) || !this.selectedPlacementIds.includes(id)
      })
    },

    // Available competitors for third place #2 (exclude other selections)
    availableForThird2 () {
      return this.bracketCompetitors.filter(c => {
        const id = c._id || c.id
        if (!this.placementMulti) {
          return id === this.placement.third2 ||
                 !this.selectedPlacementIds.includes(id)
        }
        const selected = Array.isArray(this.placement.third2) ? this.placement.third2 : []
        return selected.includes(id) || !this.selectedPlacementIds.includes(id)
      })
    }
  },

  watch: {
    matches: {
      handler () {
        this.computeLayouts()
        this.$nextTick(() => {
          this.computeConnectors()
        })
      },
      deep: true,
      immediate: true
    },
    rounds: {
      handler () {
        this.$nextTick(() => {
          this.computeConnectors()
        })
      },
      deep: true
    },
    finalWinner: {
      handler () {
        this.$nextTick(() => {
          this.computeConnectors()
        })
      }
    },
    bracket: {
      handler (newBracket) {
        if (!newBracket) return

        if (this.placementMulti) {
          const multi = newBracket.placement_multi || {}
          const firstArr = Array.isArray(multi.first) ? multi.first : []
          const secondArr = Array.isArray(multi.second) ? multi.second : []
          const thirdArr = Array.isArray(multi.third) ? multi.third : []
          const third2Arr = Array.isArray(multi.third2) ? multi.third2 : []

          const hasAnyMulti =
            firstArr.length > 0 ||
            secondArr.length > 0 ||
            thirdArr.length > 0 ||
            third2Arr.length > 0

          if (hasAnyMulti) {
            this.placement = {
              first: firstArr,
              second: secondArr,
              third: thirdArr,
              third2: third2Arr
            }
            return
          }

          // Fallback: show existing legacy placement values as single-item arrays
          // so "Generalni plasman" starts with the same placements as the bracket page.
          const legacy = newBracket.placement || {}
          this.placement = {
            first: legacy.first != null && legacy.first !== '' ? [legacy.first] : [],
            second: legacy.second != null && legacy.second !== '' ? [legacy.second] : [],
            third: legacy.third != null && legacy.third !== '' ? [legacy.third] : [],
            third2: legacy.third2 != null && legacy.third2 !== '' ? [legacy.third2] : []
          }
          return
        }

        if (newBracket.placement) {
          this.placement = {
            first: newBracket.placement.first ?? null,
            second: newBracket.placement.second ?? null,
            third: newBracket.placement.third ?? null,
            third2: newBracket.placement.third2 ?? null
          }
        }

        // Keep the podium in step with the tree. On a plain load this only fills blanks; right after
        // the admin edited a result it is authoritative, so the tree wins over a stale podium.
        this.$nextTick(() => {
          this.syncPlacementFromBracket()
        })
      },
      deep: true,
      immediate: true
    },
    compactPrint () {
      this.$nextTick(() => {
        this.computeLayouts()
        this.computeConnectors()
      })
    },
    printTeamLayout () {
      this.$nextTick(() => {
        this.computeLayouts()
        this.computeConnectors()
      })
    }
  },

  mounted () {
    this.computeLayouts()
    this.$nextTick(() => {
      this.computeConnectors()
    })
    window.addEventListener('resize', this.handleResize)
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.handleResize)
  },

  methods: {
    rememberMatchBeforeAutoEdit (matchId) {
      if (!matchId) return
      const key = String(matchId)
      if (this.placementAutoEdits[key]) return
      const m = this.matchesById && this.matchesById[matchId] ? this.matchesById[matchId] : null
      if (!m) return
      this.$set(this.placementAutoEdits, key, {
        matchId: m.id,
        round: m.round,
        matchNumber: m.matchNumber,
        before: {
          competitorRegId: m.competitorRegId ?? null,
          competitorTwoRegId: m.competitorTwoRegId ?? null,
          winnerRegId: m.winnerRegId ?? null
        }
      })
    },

    hasAnyPlacementSelected () {
      if (this.placementMulti) {
        const values = [this.placement.first, this.placement.second, this.placement.third, this.placement.third2]
        return values.some(v => Array.isArray(v) ? v.length > 0 : (v != null && v !== ''))
      }
      return (
        (this.placement.first != null && this.placement.first !== '') ||
        (this.placement.second != null && this.placement.second !== '') ||
        (this.placement.third != null && this.placement.third !== '') ||
        (this.placement.third2 != null && this.placement.third2 !== '')
      )
    },

    async revertPlacementAutoEdits () {
      const edits = Object.values(this.placementAutoEdits || {})
      if (!edits || edits.length === 0) return

      // 1) Clear winners from latest to earliest so downstream slots get cleared first.
      const byLatestFirst = edits
        .slice()
        .sort((a, b) => {
          const ra = a.round || 0
          const rb = b.round || 0
          if (ra !== rb) return rb - ra
          const ma = a.matchNumber || 0
          const mb = b.matchNumber || 0
          return mb - ma
        })

      for (const e of byLatestFirst) {
        await this.submitWinner(e.matchId, null)
      }

      // 2) Restore competitors for all touched matches.
      for (const e of edits) {
        const before = e.before || {}
        await this.setMatchCompetitors(
          e.matchId,
          before.competitorRegId ?? null,
          before.competitorTwoRegId ?? null
        )
      }

      // 3) Restore winners from earliest to latest (rebuilds propagation deterministically).
      const byEarliestFirst = edits
        .slice()
        .sort((a, b) => {
          const ra = a.round || 0
          const rb = b.round || 0
          if (ra !== rb) return ra - rb
          const ma = a.matchNumber || 0
          const mb = b.matchNumber || 0
          return ma - mb
        })

      for (const e of byEarliestFirst) {
        const w = e.before?.winnerRegId
        if (w != null && w !== '') {
          await this.submitWinner(e.matchId, w)
        }
      }

      // Clear history after successful restore.
      this.placementAutoEdits = {}
    },

    findFirstMatchForCompetitor (regId) {
      if (regId == null || regId === '') return null
      const idStr = String(regId)
      // Prefer the earliest round (e.g. semifinals) when the same reg appears in later rounds too.
      // Raw `matches` order is not guaranteed; using `.find()` alone can pick the final and break propagation.
      const sorted = (this.matches || []).slice().sort((a, b) => {
        const ra = a.round || 0
        const rb = b.round || 0
        if (ra !== rb) return ra - rb
        return (a.matchNumber || 0) - (b.matchNumber || 0)
      })
      return sorted.find(m =>
        (m.competitorRegId != null && String(m.competitorRegId) === idStr) ||
        (m.competitorTwoRegId != null && String(m.competitorTwoRegId) === idStr)
      ) || null
    },

    async submitWinner (matchId, winnerId) {
      if (!matchId) return
      this.rememberMatchBeforeAutoEdit(matchId)
      await this.$store.dispatch('tournaments/submitMatchResult', {
        matchId,
        winnerId
      })
    },

    async setMatchCompetitors (matchId, competitorRegId, competitorTwoRegId) {
      if (!matchId) return
      this.rememberMatchBeforeAutoEdit(matchId)
      await this.$store.dispatch('tournaments/updateMatchCompetitors', {
        matchId,
        competitorRegId: competitorRegId != null && competitorRegId !== '' ? competitorRegId : null,
        competitorTwoRegId: competitorTwoRegId != null && competitorTwoRegId !== '' ? competitorTwoRegId : null
      })
    },

    /**
     * Advance competitor by setting them as winner through their `nextMatchId` chain.
     * Stops before setting winner on `stopAtMatchId` (if provided), or when there is no next match.
     */
    async advanceCompetitorTo (regId, { stopAtMatchId = null, includeStopMatchWinner = false } = {}) {
      const start = this.findFirstMatchForCompetitor(regId)
      if (!start) return { reachedMatchId: null, lastWonMatchId: null }

      let current = start
      let lastWonMatchId = null

      while (current) {
        const shouldStopHere = stopAtMatchId != null && String(current.id) === String(stopAtMatchId)
        if (shouldStopHere && !includeStopMatchWinner) {
          return { reachedMatchId: current.id, lastWonMatchId }
        }

        await this.submitWinner(current.id, regId)
        lastWonMatchId = current.id

        if (shouldStopHere) {
          return { reachedMatchId: current.id, lastWonMatchId }
        }

        if (!current.nextMatchId) {
          return { reachedMatchId: current.id, lastWonMatchId }
        }

        current = this.matchesById[current.nextMatchId] || null
      }

      return { reachedMatchId: null, lastWonMatchId }
    },

    /**
     * Advance competitor until they are propagated into `targetMatchId` (i.e. by winning the match that feeds it).
     * Does NOT set winner on the target match.
     */
    async advanceCompetitorIntoMatch (regId, targetMatchId) {
      const start = this.findFirstMatchForCompetitor(regId)
      if (!start || !targetMatchId) return null

      const chainToWin = []
      let current = start

      // First verify that targetMatchId is reachable from this competitor path.
      // Do not mutate match winners unless we can actually place the competitor into target.
      while (current) {
        if (!current.nextMatchId) return null

        chainToWin.push(current)

        // Current match feeds target: this is the last match we need to win.
        if (String(current.nextMatchId) === String(targetMatchId)) {
          for (const match of chainToWin) {
            await this.submitWinner(match.id, regId)
          }
          return current.id
        }

        current = this.matchesById[current.nextMatchId] || null
      }

      return null
    },

    /**
     * Apply placement to bracket by propagating winners/participants through rounds.
     * This is admin-only behavior and intentionally overwrites existing winners where needed.
     */
    async propagatePlacementThroughBracket () {
      if (!this.isAdmin) return
      if (!this.finalMatch) return
      if (!this.rounds || this.rounds.length === 0) return

      // Always revert previous auto-propagation first so removing/changing podium selections
      // returns the bracket to the exact prior state.
      await this.revertPlacementAutoEdits()

      const first = this.placement.first
      const second = this.placement.second
      const third = this.placement.third
      const third2 = this.placement.third2

      const hasFirst = first != null && first !== ''
      if (!hasFirst) return

      const totalRounds = this.rounds.length

      // 1 round (<=2 competitors): final is the only match
      if (totalRounds === 1) {
        if (second != null && second !== '') {
          await this.setMatchCompetitors(this.finalMatch.id, first, second)
        }
        // Winner must be one of participants, so only submit if valid.
        const c1 = this.finalMatch.competitorRegId != null ? String(this.finalMatch.competitorRegId) : null
        const c2 = this.finalMatch.competitorTwoRegId != null ? String(this.finalMatch.competitorTwoRegId) : null
        const w = String(first)
        if (w === c1 || w === c2 || (second != null && second !== '')) {
          await this.submitWinner(this.finalMatch.id, first)
        }
        return
      }

      // 2+ rounds: drive propagation using the draw structure (`nextMatchId` chain).
      // Do not assign 1st place to "semifinal A" by matchNumber — the gold medalist may have come from the other half.
      // Advance 1st all the way to final (and set final winner).
      await this.advanceCompetitorTo(first)

      // Advance 2nd to final slot (by winning up to their semifinal). Do NOT set final winner to 2nd.
      if (second != null && second !== '') {
        // If their chain reaches the final, `advanceCompetitorTo` would set them as final winner, which we don't want.
        // So stop at the match that feeds into the final.
        await this.advanceCompetitorIntoMatch(second, this.finalMatch.id)
      }

      // Place 3rd/4th as semifinal losers for the side that 1st/2nd came from.
      // Convention: 3rd loses to 1st, 4th loses to 2nd.
      const semifinalRound = this.rounds[this.rounds.length - 2]
      const semis = (semifinalRound?.matches || [])

      const semiWonBy = (regId) => semis.find(m => m.winnerRegId != null && String(m.winnerRegId) === String(regId)) || null

      const semiForFirst = semiWonBy(first)
      const semiForSecond = second != null && second !== '' ? semiWonBy(second) : null

      if (third != null && third !== '' && semiForFirst) {
        await this.advanceCompetitorIntoMatch(third, semiForFirst.id)
        // Ensure semifinal winner is still first (3rd must lose here)
        await this.submitWinner(semiForFirst.id, first)
      }

      if (third2 != null && third2 !== '' && semiForSecond) {
        await this.advanceCompetitorIntoMatch(third2, semiForSecond.id)
        await this.submitWinner(semiForSecond.id, second)
      }

      // Finally ensure final winner is 1st (in case anything changed while populating other side).
      await this.submitWinner(this.finalMatch.id, first)
    },
    computeLayouts () {
      this.$nextTick(() => {
        const layouts = {}
        const roundHeights = {}
        const heights = {}

        const fallbackHeight = this.printTeamLayout ? 200 : (this.compactPrint ? 44 : 120)
        // When printTeamLayout, use fixed height so layout and connectors match print CSS; otherwise measure DOM
        this.rounds.forEach(round => {
          round.matches.forEach(match => {
            if (this.printTeamLayout) {
              heights[match.id] = 200
            } else {
              const ref = this.$refs[`match-${match.id}`]
              const el = Array.isArray(ref) ? ref[0] : ref
              heights[match.id] = el?.offsetHeight || fallbackHeight
            }
          })
        })

        this.rounds.forEach((round, roundIndex) => {
          if (roundIndex === 0) {
            let top = 0
            round.matches.forEach(match => {
              const height = heights[match.id]
              layouts[match.id] = { top, height }
              top += height + this.effectiveBaseMatchGap
            })
            const lastMatch = round.matches[round.matches.length - 1]
            const lastHeight = lastMatch ? heights[lastMatch.id] : 0
            roundHeights[round.number] = lastMatch ? (layouts[lastMatch.id].top + lastHeight) : top
            return
          }

          const previousRound = this.rounds[roundIndex - 1]
          let fallbackTop = 0

          round.matches.forEach(match => {
            const height = heights[match.id]
            const sourceMatches = previousRound.matches.filter(m => m.nextMatchId === match.id)
            let top

            if (sourceMatches.length) {
              const centers = sourceMatches
                .map(m => {
                  const layout = layouts[m.id]
                  if (!layout) return null
                  return layout.top + (layout.height / 2)
                })
                .filter(center => center !== null)

              if (centers.length) {
                const minCenter = Math.min(...centers)
                const maxCenter = Math.max(...centers)
                const center = (minCenter + maxCenter) / 2
                top = center - (height / 2)
              }
            }

            if (top === undefined) {
              top = fallbackTop
            }

            layouts[match.id] = { top, height }
            fallbackTop = Math.max(fallbackTop, top + height + this.effectiveBaseMatchGap)
          })

          const roundBottom = round.matches.reduce((max, match) => {
            const layout = layouts[match.id]
            if (!layout) return max
            return Math.max(max, layout.top + layout.height)
          }, 0)

          roundHeights[round.number] = roundBottom + (this.effectiveBaseMatchGap / 2)
        })

        this.matchLayouts = layouts
        this.roundHeights = roundHeights
      })
    },

    getMatchPositionStyle (match) {
      const layout = this.matchLayouts[match.id]
      if (!layout) return {}

      return {
        position: 'absolute',
        top: `${layout.top}px`,
        left: 0,
        right: 0
      }
    },

    getRoundContainerStyle (round) {
      const height = this.roundHeights[round.number]
      return height ? { height: `${height}px` } : {}
    },

    isMedalHolder (competitorReg) {
      if (!competitorReg) return false
      return !!(competitorReg.is_medal_holder || competitorReg.is_seeded || competitorReg.isSeeded)
    },

    getRoundName (roundNumber) {
      const totalRounds = this.rounds.length

      if (roundNumber === totalRounds) {
        return 'Finale'
      } else if (roundNumber === totalRounds - 1) {
        return 'Polufinale'
      } else if (roundNumber === totalRounds - 2) {
        return 'Četvrtfinale'
      } else {
        return this.$tc('tournament.eliminations')
      }
    },

    isFirstRound (match) {
      if (!match || this.rounds.length === 0) return false
      return match.round === this.rounds[0].number
    },

    getCompetitorName (competitorReg) {
      if (!competitorReg) {
        return this.$tc('tournament.unknown')
      }

      // If this is a team registration, display club name followed by competitors in parentheses
      if (competitorReg.isTeam && competitorReg.teamMembers && competitorReg.teamMembers.length > 0) {
        const teamMembersStr = competitorReg.teamMembers.map(member => {
          const name = `${member.firstName || ''} ${member.lastName || ''}`.trim()
          const gender = member.isMale ? 'M' : 'Ž'
          const numberPrefix = member.assignedNumber ? `#${member.assignedNumber} ` : ''
          return `${numberPrefix}${name} (${gender})`
        }).join(', ')

        const clubName = this.getCompetitorClubName(competitorReg)
        return clubName ? `${clubName} (${teamMembersStr})` : teamMembersStr
      }

      // Individual competitor display
      if (!competitorReg.competitor) {
        return this.$tc('tournament.unknown')
      }

      const competitor = competitorReg.competitor
      const name = `${competitor.firstName || ''} ${competitor.lastName || ''}`.trim()
      const gender = competitor.isMale === true ? ' (M)' : (competitor.isMale === false ? ' (Ž)' : '')
      const assignedNumber = competitorReg.assignedNumber ? ` (#${competitorReg.assignedNumber})` : ''

      return name ? `${name}${gender}${assignedNumber}` : this.$tc('tournament.unknown')
    },

    getCompetitorClubName (competitorReg) {
      if (!competitorReg || !competitorReg.club) return ''
      const club = competitorReg.club
      return (club.shortName || club.name || '').trim()
    },

    getPlacementCompetitorLabel (competitorReg, position) {
      const name = this.getCompetitorName(competitorReg)
      const clubName = this.getCompetitorClubName(competitorReg)
      const medalByPosition = {
        first: '🥇',
        second: '🥈',
        third: '🥉',
        third2: '🥉'
      }
      const medal = medalByPosition[position] || ''
      const competitorWithClub = clubName ? `${name} - ${clubName}` : name
      return medal ? `${medal} ${competitorWithClub}` : competitorWithClub
    },

    isMatchClickable (match) {
      // Match is clickable if:
      // 1. Bracket is locked (isLocked === true) OR user is admin
      // 2. At least one participant is present (BYE matches are allowed)
      // Admins can always edit matches regardless of locked state, winner status, or competitor presence
      // Admins can edit any round at any time, including empty matches, matches with winners, etc.
      if (this.isAdmin) {
        // Admins can always edit matches, even when empty
        return true
      }
      // Non-admins require locked brackets and at least one competitor
      return (
        this.isLocked &&
        (match.competitorReg || match.competitorTwoReg)
      )
    },

    handleMatchClick (match) {
      if (!this.isMatchClickable(match)) {
        return
      }

      this.selectedMatch = match
      this.showModal = true
    },

    handleResultSubmitted () {
      // The admin changed a match result, so the tree now outranks whatever the podium holds.
      this.pendingAuthoritativePlacementSync = true
      // Emit event to parent to refetch brackets
      this.$emit('result-submitted')
      this.showModal = false
      this.selectedMatch = null
    },

    getRoundMatchesStyle (round) {
      return this.getRoundContainerStyle(round)
    },

    // Get match label for any match (all rounds)
    getMatchLabel (match) {
      return this.matchLabels[match.id] || null
    },

    // Get placeholder text for a competitor slot in a future match
    getPlaceholderText (match, slotNumber) {
      // Only show placeholders in rounds after Round 1
      if (this.rounds.length === 0 || match.round === this.rounds[0].number) {
        return null
      }

      const sources = this.matchSlotSources[match.id]
      if (!sources) return null

      const label = slotNumber === 1 ? sources.competitor1Label : sources.competitor2Label
      if (label) {
        return `${this.$tc('tournament.winner_of_match')} ${label}`
      }

      return null
    },

    handleResize () {
      this.computeLayouts()
      this.$nextTick(() => {
        this.computeConnectors()
      })
    },

    computeConnectors () {
      this.$nextTick(() => {
        // Use layout-based when compact print or team print layout (so connector paths match box positions)
        if ((this.compactPrint || this.printTeamLayout) && Object.keys(this.matchLayouts).length > 0 && Object.keys(this.roundHeights).length > 0) {
          this.computeConnectorsFromLayout()
          return
        }

        const paths = []
        const bracketDisplayEl = this.$el.querySelector('.bracket-display')
        if (!bracketDisplayEl) return

        const bracketRect = bracketDisplayEl.getBoundingClientRect()
        this.svgWidth = bracketRect.width
        this.svgHeight = bracketRect.height

        for (let roundIndex = 0; roundIndex < this.rounds.length - 1; roundIndex++) {
          const currentRound = this.rounds[roundIndex]
          const nextRound = this.rounds[roundIndex + 1]
          const targetToSources = {}
          currentRound.matches.forEach(sourceMatch => {
            if (sourceMatch.isBye && !sourceMatch.competitorReg && !sourceMatch.competitorTwoReg) return
            if (!sourceMatch.nextMatchId) return
            if (!targetToSources[sourceMatch.nextMatchId]) targetToSources[sourceMatch.nextMatchId] = []
            targetToSources[sourceMatch.nextMatchId].push(sourceMatch)
          })

          Object.keys(targetToSources).forEach(targetMatchId => {
            const sourceMatches = targetToSources[targetMatchId]
            const targetMatch = nextRound.matches.find(m => String(m.id) === String(targetMatchId))
            if (!targetMatch || sourceMatches.length === 0) return

            const targetMatchEl = bracketDisplayEl.querySelector(`[data-match-id="${targetMatch.id}"]`)
            if (!targetMatchEl) return
            const targetRect = targetMatchEl.getBoundingClientRect()
            const targetX = targetRect.left - bracketRect.left
            const targetY = targetRect.top + targetRect.height / 2 - bracketRect.top

            const sourcePositions = []
            sourceMatches.forEach(sourceMatch => {
              const sourceMatchEl = bracketDisplayEl.querySelector(`[data-match-id="${sourceMatch.id}"]`)
              if (!sourceMatchEl) return
              const sourceRect = sourceMatchEl.getBoundingClientRect()
              sourcePositions.push({
                x: sourceRect.right - bracketRect.left,
                y: sourceRect.top + sourceRect.height / 2 - bracketRect.top,
                matchId: sourceMatch.id
              })
            })
            if (sourcePositions.length === 0) return

            const pathData = this.buildConnectorPath(sourcePositions, targetX, targetY)
            paths.push({
              d: pathData,
              sourceMatchId: sourcePositions.map(p => p.matchId).join(','),
              targetMatchId: targetMatch.id
            })
          })
        }

        if (this.finalMatch) {
          const finalMatchEl = bracketDisplayEl.querySelector(`[data-match-id="${this.finalMatch.id}"]`)
          const winnerMatchEl = bracketDisplayEl.querySelector('.winner-match')
          if (finalMatchEl && winnerMatchEl) {
            const finalRect = finalMatchEl.getBoundingClientRect()
            const winnerRect = winnerMatchEl.getBoundingClientRect()
            const finalX = finalRect.right - bracketRect.left
            const finalY = finalRect.top + finalRect.height / 2 - bracketRect.top
            const winnerX = winnerRect.left - bracketRect.left
            paths.push({
              d: `M ${finalX} ${finalY} L ${winnerX} ${finalY}`.trim(),
              sourceMatchId: this.finalMatch.id,
              targetMatchId: 'winner'
            })
          }
        }

        this.connectorPaths = paths
      })
    },

    computeConnectorsFromLayout () {
      const paths = []
      const pad = this.layoutPadding
      const headerH = this.layoutRoundHeaderHeight
      const roundWidth = this.layoutRoundWidth
      const gap = this.layoutGap

      const roundLeft = []
      for (let i = 0; i <= this.rounds.length; i++) {
        roundLeft[i] = pad.left + i * (roundWidth + gap)
      }

      const maxRoundHeight = Math.max(...Object.values(this.roundHeights), 0)
      this.svgHeight = pad.top + headerH + maxRoundHeight
      this.svgWidth = roundLeft[this.rounds.length]

      const getMatchCenter = (matchId, roundIndex) => {
        const layout = this.matchLayouts[matchId]
        if (!layout) return null
        const y = pad.top + headerH + layout.top + layout.height / 2
        const xRight = roundLeft[roundIndex] + roundWidth
        const xLeft = roundLeft[roundIndex]
        return { xLeft, xRight, y }
      }

      for (let roundIndex = 0; roundIndex < this.rounds.length - 1; roundIndex++) {
        const currentRound = this.rounds[roundIndex]
        const nextRound = this.rounds[roundIndex + 1]
        const targetToSources = {}
        currentRound.matches.forEach(sourceMatch => {
          if (sourceMatch.isBye && !sourceMatch.competitorReg && !sourceMatch.competitorTwoReg) return
          if (!sourceMatch.nextMatchId) return
          if (!targetToSources[sourceMatch.nextMatchId]) targetToSources[sourceMatch.nextMatchId] = []
          targetToSources[sourceMatch.nextMatchId].push(sourceMatch)
        })

        Object.keys(targetToSources).forEach(targetMatchId => {
          const sourceMatches = targetToSources[targetMatchId]
          const targetMatch = nextRound.matches.find(m => String(m.id) === String(targetMatchId))
          if (!targetMatch || sourceMatches.length === 0) return

          const targetPos = getMatchCenter(targetMatch.id, roundIndex + 1)
          if (!targetPos) return

          const sourcePositions = []
          sourceMatches.forEach(sourceMatch => {
            const pos = getMatchCenter(sourceMatch.id, roundIndex)
            if (!pos) return
            sourcePositions.push({
              x: pos.xRight,
              y: pos.y,
              matchId: sourceMatch.id
            })
          })
          if (sourcePositions.length === 0) return

          const pathData = this.buildConnectorPath(
            sourcePositions,
            targetPos.xLeft,
            targetPos.y
          )
          paths.push({
            d: pathData,
            sourceMatchId: sourcePositions.map(p => p.matchId).join(','),
            targetMatchId: targetMatch.id
          })
        })
      }

      if (this.finalMatch) {
        const finalPos = getMatchCenter(this.finalMatch.id, this.rounds.length - 1)
        if (finalPos) {
          const winnerX = roundLeft[this.rounds.length]
          paths.push({
            d: `M ${finalPos.xRight} ${finalPos.y} L ${winnerX} ${finalPos.y}`.trim(),
            sourceMatchId: this.finalMatch.id,
            targetMatchId: 'winner'
          })
        }
      }

      this.connectorPaths = paths
    },

    buildConnectorPath (sourcePositions, targetX, targetY) {
      let pathData = ''
      if (sourcePositions.length === 1) {
        const source = sourcePositions[0]
        const midX = (source.x + targetX) / 2
        pathData = `M ${source.x} ${source.y} L ${midX} ${source.y} L ${midX} ${targetY} L ${targetX} ${targetY}`
      } else {
        sourcePositions.sort((a, b) => a.y - b.y)
        const minY = sourcePositions[0].y
        const maxY = sourcePositions[sourcePositions.length - 1].y
        const verticalMidY = (minY + maxY) / 2
        const avgSourceX = sourcePositions.reduce((sum, pos) => sum + pos.x, 0) / sourcePositions.length
        const verticalX = avgSourceX + (targetX - avgSourceX) * 0.5

        sourcePositions.forEach(source => {
          pathData += `M ${source.x} ${source.y} L ${verticalX} ${source.y} `
        })
        pathData += `M ${verticalX} ${minY} L ${verticalX} ${maxY} `
        pathData += `M ${verticalX} ${verticalMidY} L ${targetX} ${verticalMidY} `
        pathData += `M ${targetX} ${targetY} L ${targetX} ${verticalMidY}`
      }
      return pathData.trim().replace(/\s+/g, ' ')
    },

    handleWinnerClick () {
      // Allow admins to edit the winner by opening the winner edit modal
      if (!this.isAdmin || !this.finalMatch) {
        return
      }

      this.showWinnerModal = true
    },

    handleWinnerSaved () {
      // Picking (or removing) the winner in the modal must move the podium below the draw.
      this.pendingAuthoritativePlacementSync = true
      // Reload brackets after winner is saved
      this.$emit('result-submitted')
    },

    /**
     * Writes the podium implied by the match tree into the Plasman selects.
     *
     * Two modes, because a page load must not silently rewrite standings an official entered by hand:
     * - fill-only (plain reload): sets slots the tree decided and the podium left empty.
     * - authoritative (right after a result/winner edit): the tree replaces the whole podium,
     *   including clearing slots the tree can no longer decide.
     *
     * Skipped for the Bodovanje sheet (`placementOnly`) and "Generalni plasman" (`placementMulti`),
     * where the podium comes from scores/ties rather than from a draw.
     */
    async syncPlacementFromBracket () {
      const authoritative = this.pendingAuthoritativePlacementSync
      this.pendingAuthoritativePlacementSync = false

      if (!this.isAdmin || this.placementMulti || this.placementOnly) return
      if (this.placementSyncBusy) return

      const derived = this.derivedPlacement
      if (!derived) return

      const keys = ['first', 'second', 'third', 'third2']
      const isEmpty = (value) => value === null || value === undefined || value === ''
      const sameId = (a, b) => String(a ?? '') === String(b ?? '')

      const next = { ...this.placement }
      keys.forEach(key => {
        if (authoritative) {
          // Derived slots are distinct by construction (a competitor loses at most one of these matches).
          next[key] = derived[key]
          return
        }
        if (isEmpty(derived[key]) || !isEmpty(next[key])) return
        // The backend rejects the same competitor in two slots, so never fill a blank with someone
        // an official already placed elsewhere by hand.
        const takenElsewhere = keys.some(other => other !== key && sameId(next[other], derived[key]) && !isEmpty(next[other]))
        if (takenElsewhere) return
        next[key] = derived[key]
      })

      if (keys.every(key => sameId(next[key], this.placement[key]))) return

      this.placementSyncBusy = true
      try {
        this.placement = next
        // The values came from the tree, so propagating them back into it would be a no-op at best.
        // A fill-only repair also skips the reload: several brackets can repair on the same page load,
        // and one full refetch each would stampede. Patch the local bracket instead.
        await this.persistPlacement({ propagate: false, reload: authoritative })
        if (!authoritative) {
          this.$set(this.bracket, 'placement', { ...next })
        }
      } finally {
        this.placementSyncBusy = false
      }
    },

    /**
     * Fills the podium from an externally computed ranking (currently the Bodovanje score sheet).
     *
     * Reuses savePlacement so the placement endpoint — and the ranking points it moves — runs
     * exactly as it does for a manual pick. Returns false when the podium already matches, so the
     * caller can stay quiet instead of announcing a change that did not happen.
     *
     * @param {{first: number|null, second: number|null, third: number|null}} ranking
     * @returns {Promise<boolean>} whether anything was saved
     */
    async applyAutoPlacement (ranking) {
      if (!ranking) return false

      const wrap = (id) => {
        if (id === null || id === undefined) return this.placementMulti ? [] : null
        return this.placementMulti ? [id] : id
      }

      const next = {
        first: wrap(ranking.first ?? null),
        second: this.placementPositions >= 2 ? wrap(ranking.second ?? null) : wrap(null),
        third: this.placementPositions >= 3 ? wrap(ranking.third ?? null) : wrap(null),
        // Scoring produces a single third place; the bracket-only "3. mesto #2" slot stays as is.
        third2: this.placement.third2
      }

      const same = ['first', 'second', 'third'].every(
        key => JSON.stringify(this.placement[key] ?? null) === JSON.stringify(next[key] ?? null)
      )
      if (same) return false

      this.placement = { ...this.placement, ...next }
      await this.savePlacement()
      return true
    },

    // Bound to the selects' @change, which hands us the picked value — hence no options here.
    async savePlacement () {
      await this.persistPlacement({ propagate: true })
    },

    /**
     * @param {{propagate?: boolean, reload?: boolean}} options
     *   `propagate: false` when the podium was just read out of the tree, so pushing it back through
     *   the draw would only churn match rows.
     *   `reload: false` to skip the parent refetch when the caller patches local state itself.
     */
    async persistPlacement ({ propagate = true, reload = true } = {}) {
      if (!this.bracket || !this.bracket.id) return
      try {
        const placementPayload = {
          first: this.placement.first,
          second: this.placement.second,
          third: this.placement.third,
          third2: this.placement.third2
        }

        if (this.placementMulti) {
          await this.$store.dispatch('tournaments/updateBracketPlacementMulti', {
            bracketId: this.bracket.id,
            placement: placementPayload
          })
          if (reload) this.$emit('result-submitted')
          return
        }

        await this.$store.dispatch('tournaments/updateBracketPlacement', {
          bracketId: this.bracket.id,
          placement: placementPayload
        })

        if (propagate) {
          // If placement is being cleared/changed, revert any previous auto-propagation first.
          // Then (if any placement remains) apply propagation again.
          await this.revertPlacementAutoEdits()
          if (this.isAdmin && this.finalMatch && this.hasAnyPlacementSelected() && !this.placementMulti) {
            await this.propagatePlacementThroughBracket()
          }
        }

        if (reload) this.$emit('result-submitted')
      } catch (err) {
        this.$message.error(err.response?.data?.error || this.$tc('general.save_failed'))
      }
    },

    getWinnerContainerStyle () {
      // Match the height of the final round to ensure proper alignment
      if (!this.finalMatch) return {}

      const finalRound = this.rounds[this.rounds.length - 1]
      const height = this.roundHeights[finalRound.number]

      return height ? { height: `${height}px` } : {}
    },

    getWinnerMatchStyle () {
      // Position the winner match centered vertically relative to the final match
      if (!this.finalMatch) return {}

      const layout = this.matchLayouts[this.finalMatch.id]
      if (!layout) return {}

      // Calculate the center position of the Final match
      const finalCenterY = layout.top + (layout.height / 2)

      return {
        position: 'absolute',
        top: `${finalCenterY}px`,
        left: 0,
        right: 0,
        transform: 'translateY(-50%)'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.bracket-display-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: stretch;
}

.bracket-display {
  display: flex;
  overflow: visible;
  padding: 20px 40px;
  gap: 60px;
  position: relative;
  width: 100%;
  flex-shrink: 0;
  align-self: stretch;
}

.bracket-connectors {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.connector-line {
  fill: none;
  stroke: #c0c4cc;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.bracket-round {
  min-width: 250px;
  flex-shrink: 0;
  position: relative;
  z-index: 2;
}

.round-header {
  margin-bottom: 15px;
  text-align: center;

  h4 {
    margin: 0;
    font-size: 16px;
    font-weight: bold;
    color: #333;
  }
}

.round-matches {
  display: block;
  position: relative;
  min-height: 120px;
  overflow: visible;
}

.match-container {
  position: absolute;
  width: 100%;
  z-index: 3;
}

.match-label-inline {
  font-size: 11px;
  font-weight: 600;
  color: #909399;
  text-align: center;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e0e0e0;
}

.match-box {
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  background: #fff;
  min-height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
  position: relative;

  &.is-clickable {
    cursor: pointer;

    &:hover {
      border-color: #409eff;
      box-shadow: 0 4px 12px rgba(64, 158, 255, 0.25);
      transform: translateY(-2px);
    }

    &:active {
      transform: translateY(0);
    }
  }

  &.is-completed {
    border-color: #67c23a;
    background-color: #f0f9ff;
    opacity: 0.9;
    cursor: default;
  }

  &.is-disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background-color: #f5f5f5;

    &:hover {
      border-color: #e0e0e0;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      transform: none;
    }
  }
}

.bracket-display-compact {
  .round-header {
    margin-bottom: 4px;

    h4 {
      font-size: 10px;
    }
  }

  .round-matches {
    min-height: 44px;
  }

  .match-label-inline {
    font-size: 8px;
    margin-bottom: 2px;
    padding-bottom: 2px;
  }

  .match-box {
    min-height: 44px;
    padding: 3px 4px;
  }

  .competitor {
    font-size: 9px;
    min-height: 18px;
    padding: 2px 4px;
  }

  .vs-divider {
    padding: 2px 0;
    font-size: 8px;
  }

  .winner-box {
    min-height: 44px;
    padding: 3px 4px;
    font-size: 9px;
  }
}

.competitor {
  padding: 8px;
  border-radius: 4px;
  font-size: 14px;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  background-color: transparent;
  text-align: center;
  width: 100%;
  box-sizing: border-box;

  &.is-winner {
    background-color: #e8f5e9;
    font-weight: bold;
    color: #2e7d32;
    margin-left: 2px;
    margin-right: 2px;
    width: calc(100% - 4px);
  }

  &.is-bye {
    background-color: #f5f5f5;
    color: #999;
    font-style: italic;
  }
}

.vs-divider {
  text-align: center;
  padding: 8px 0;
  font-size: 12px;
  color: #999;
  font-weight: bold;
  width: 100%;
}

.empty-slot {
  color: #ccc;
  font-style: italic;
}

.competitor-name-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.competitor-club-line {
  font-size: 12px;
  color: #606266;
}

.placeholder-text {
  color: #909399;
  font-style: italic;
  font-size: 13px;
}

.bye-text {
  color: #999;
  font-style: italic;
}

.seed-badge {
  display: inline-block;
  background-color: #ff9800;
  color: white;
  font-size: 11px;
  font-weight: bold;
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 8px;
}

.team-entry {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.team-entry-club {
  font-weight: 600;
  font-size: 14px;
}

.team-member-names {
  font-size: 12px;
  color: #606266;
  line-height: 1.4;
}

.team-member-name {
  line-height: 1.5;
  margin: 2px 0;
}

.team-member-number {
  display: inline-block;
  margin-right: 4px;
  font-weight: 600;
  color: #409eff;
}

.bracket-round {
  position: relative;
}

.winner-round {
  min-width: 180px;
}

.winner-container {
  min-height: 120px;
  display: block;
  position: relative;
  overflow: visible;
}

.winner-match {
  position: absolute;
  width: 100%;
  z-index: 3;
}

.winner-box {
  border: 2px solid #67c23a;
  background-color: #f0f9ff;
  min-height: 60px;
  padding: 8px 10px;
}

.winner-competitor {
  font-weight: bold;
  font-size: 14px;
  color: #2e7d32;
  width: 100%;
}

.placement-section {
  margin-top: 40px;
  padding: 20px 40px;
  width: 100%;
  box-sizing: border-box;
  flex-shrink: 0;
  clear: both;
}

.placement-title {
  margin: 0 0 20px 0;
  font-size: 16px;
  font-weight: bold;
  color: #333;
}

.placement-fields {
  display: flex;
  gap: 20px;
  flex-wrap: nowrap;
  align-items: flex-start;
}

.placement-field {
  flex: 1;
  min-width: 0;
}

.placement-label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  color: #606266;
  font-weight: 500;
}

.placement-select {
  width: 100%;
}

// Print styles - match Draw page: rounds side by side, connector lines visible
@media print {
  .bracket-display-wrapper {
    overflow: visible !important;
    width: 100%;
  }

  .bracket-display {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    overflow: visible !important;
    padding: 20px !important;
    gap: 40px !important;
    page-break-inside: avoid;
    background: white !important;
    position: relative !important;
    width: 100%;
  }

  .bracket-connectors {
    display: block !important;
    visibility: visible !important;
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    pointer-events: none;
    z-index: 1;
  }

  .connector-line {
    fill: none !important;
    stroke: #333 !important;
    stroke-width: 2 !important;
    stroke-linecap: round !important;
    stroke-linejoin: round !important;
  }

  .bracket-round {
    page-break-inside: avoid;
    flex-shrink: 0 !important;
    min-width: 200px !important;
  }

  .match-box {
    page-break-inside: avoid;
    border: 1px solid #333 !important;
    box-shadow: none !important;
    background: white !important;
  }

  .match-container {
    page-break-inside: avoid;
  }

  // Prevent line breaks inside match boxes
  .match-box {
    break-inside: avoid;
    break-after: auto;
  }

  .competitor {
    break-inside: avoid;
  }

  /* Team brackets: larger competitor fields so all team member names fit; heights match printTeamLayout (200px) */
  .bracket-display-wrapper.bracket-display-team {
    .round-matches {
      min-height: 240px !important;
    }

    .match-box {
      min-height: 200px !important;
      padding: 10px 12px !important;
    }

    .competitor {
      min-height: 95px !important;
      padding: 8px 10px !important;
      align-items: flex-start !important;
      justify-content: flex-start !important;
      flex-direction: column !important;
      overflow: visible !important;
    }

    .team-entry-club {
      font-size: 12px;
    }

    .team-member-names {
      font-size: 10px;
    }

    .team-member-name {
      line-height: 1.6;
      margin: 3px 0;
      font-size: 11px;
      flex-shrink: 0;
    }

    .winner-container {
      min-height: 240px !important;
    }

    .winner-box {
      min-height: 200px !important;
      padding: 10px 12px !important;
    }

    .winner-competitor {
      min-height: 95px !important;
      align-items: flex-start !important;
      justify-content: flex-start !important;
      flex-direction: column !important;
      overflow: visible !important;
    }
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .bracket-display {
    flex-direction: column;
    gap: 20px;
    padding: 20px 0;
  }

  .bracket-round {
    min-width: 100%;
  }
}

// FORCE DESKTOP LAYOUT in print - override responsive breakpoints
// CRITICAL: Only apply in print, never affect screen layout

@media print {
  /* Disable all responsive / mobile breakpoints when print-desktop-mode is active - ONLY IN PRINT */
  html.print-desktop-mode .bracket-display {
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    gap: 40px !important;
    padding: 0 !important;
  }

  html.print-desktop-mode .bracket-round {
    flex-shrink: 0 !important;
    min-width: 320px !important;
    width: auto !important;
  }
}
</style>
